import React from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '@constants/routes';
import { scrollToTop, filterDuplicateDocuments, includesId, logNetRequest, sleep } from '@utils/usefulFuncs';
import styled from 'styled-components';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { Page, Container, H5, Body5 } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { AI_TOOLS, DOCUMENT_SELECTION_MODES } from '@constants';
import { AgentScriptResult } from '@containers/results/AgentScriptResult';
import { addAdvancedSettingValues, useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { InfoIconTypes } from '@constants/infoIcons';
import InfoIconTooltip from '@ui/InfoIconTooltip';
import { DocumentSelectionModalControlled } from '@features/document/DocumentSelectionModal/DocumentSelectionModalControlled';
import { SelectedDocuments } from '@containers/DigitalAnalyst/components/SelectedDocuments';
import { FormState } from '@ui/FormState';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { handle400Statuses, sendApiDownloadRequest } from '@services/api';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { Working } from '@ui/Working';
import { SelectedDocument } from '@ui/SelectedDocument';
import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { AgentSelect } from '@containers/DigitalAnalyst/components/AgentSelect';
import { getItemFromStorage, setItemToStorage } from '@utils/storage';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;
const FormPageWrapper = styled.div`
  position: relative;
`;
const SubmitButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;
const PreviouslyGeneratedInstructionsIdButton = styled.div`
  button {
    padding-left: 0px;
  }
`;
const FormWrapper = styled.div`
  padding-top: 16px;

  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;
const Label = styled.div``;
const SourcesHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  padding-bottom: 16px;
`;
const AgentSelectWrapper = styled.div`
  width: 100%;
  padding-top: 8px;
`;
const OverrideResultIdInput = styled.input`
  width: 420px;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 5px;
  font-size: 14px;
  line-height: 18px;
`;

const SourcesHeading = () => {
  return (
    <SourcesHeadingWrapper>
      <Container right={6} bottom={2}>
        <Body5>Sources</Body5>
      </Container>
      <InfoIconTooltip type={InfoIconTypes.SOURCES} />
    </SourcesHeadingWrapper>
  );
};

const FoundationDocHeading = () => {
  return (
    <SourcesHeadingWrapper>
      <Container right={6} bottom={2}>
        <Body5>Foundation Document</Body5>
      </Container>

      <InfoIconTooltip type={InfoIconTypes.FOUNDATION_DOCUMENT} />
    </SourcesHeadingWrapper>
  );
};

const getResultIdStorage = () => {
  const resultIdStorage = getItemFromStorage('resultIdStorage');
  return JSON.parse(resultIdStorage) || [];
};

const addToResultIdStorage = (resultId) => {
  const resultIdStorage = getResultIdStorage();
  resultIdStorage.push(resultId);
  setItemToStorage('resultIdStorage', resultIdStorage);
};

// const clearResultIdStorage = () => {
//   window.localStorage.removeItem('resultIdStorage');
// };

const tool = AI_TOOLS.AGENT_SPREADSHEET;

const foundationDocSelectModes = [DOCUMENT_SELECTION_MODES.allCompany, DOCUMENT_SELECTION_MODES.myDocs];
const docSelectModes = [DOCUMENT_SELECTION_MODES.allCompany, DOCUMENT_SELECTION_MODES.myDocs];

const submissionStates = {
  idle: 'idle',
  error: 'error',

  generatingResults: 'generatingResults',
  finishedResult: 'finishedResult',
  generatingXLSFile: 'generatingXLSFile',
  finishedXLSFile: 'finishedXLSFile',
};

const defaultResultIdOptions = ['Please select...', 'Enter Manually'];

function AgentSpreadsheet() {
  const navigate = useNavigate();

  const advancedSettingsValues = useAdvancedSettings();

  const [showOverrideResultIdInput, setShowOverrideResultIdInput] = React.useState(false);
  const [overrideResultIdOptions, setOverrideResultIdOptions] = React.useState(defaultResultIdOptions);
  const [overrideResultId, setOverrideResultId] = React.useState('');
  const [selectedOverrideResultId, setSelectedOverrideResultId] = React.useState('');

  const [showManualOverrideResultIdInput, setShowManualOverrideResultIdInput] = React.useState(false);

  const [resultId, setResultId] = React.useState(null);
  const [resultMetadata, setResultMetadata] = React.useState(null);

  const [formError, setFormError] = React.useState(null);
  const [pageError, setPageError] = React.useState(null);

  const [streamStatus, setStreamStatus] = React.useState('pending');
  const [submissionState, setSubmissionState] = React.useState('idle');

  const [agentName, setAgentName] = React.useState('');
  const [question, setQuestion] = React.useState('');

  const [isSelectingDocs, setIsSelectingDocs] = React.useState(false);
  const [selectedDocs, setSelectedDocs] = React.useState([]);

  const [isSelectingFoundationDocument, setIsSelectingFoundationDocument] = React.useState(false);
  const [selectedFoundationDocument, setSelectedFoundationDocument] = React.useState([]);

  const [constructedXLSFile, setConstructedXLSFile] = React.useState(null);

  React.useEffect(() => {
    if (submissionState === submissionStates.finishedResult) {
      constructXLSFile();
    }
    if (submissionState === submissionStates.error) {
      setSubmissionState(submissionStates.idle);
    }
  }, [submissionState]);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  const handleSelectedOverrideResultIdChange = (e) => {
    const {
      target: { value },
    } = e;
    setSelectedOverrideResultId(value);
    if (value === 'Enter Manually') {
      setShowManualOverrideResultIdInput(true);
    } else {
      if (!isNaN(value)) {
        // onSetResult({ id: value, question: '', agentName: '' });
      }
      setShowManualOverrideResultIdInput(false);
    }
  };
  const handleOverrideResultIdChange = (e) => {
    const {
      target: { value },
    } = e;
    setOverrideResultId(value);
  };

  const handleRemoveFoundationDocument = (v) => {
    setSelectedFoundationDocument([]);
  };

  const onDownloadXLSFile = (fileData) => {
    const blob = new Blob([fileData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'generated-spreadsheet.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const constructXLSFile = async () => {
    setSubmissionState(submissionStates.generatingXLSFile);
    const agentScriptRecordId = overrideResultId !== '' ? overrideResultId : resultId;
    const URL = `v1/genai/ai_constructed_xlsx?agent_script_record_id=${agentScriptRecordId}&foundation_doc_id=${selectedFoundationDocument[0].id}`;
    const response = await sendApiDownloadRequest(URL);
    const { status, data, error } = response;
    logNetRequest(status, response, URL);
    handle400Statuses(status);

    if (status === 200) {
      const blob = await response.blob();
      setFormError(null);
      setConstructedXLSFile(blob);
      onDownloadXLSFile(blob);
      setSubmissionState(submissionStates.finishedXLSFile);
    } else {
      const json = await response.json();
      console.log('json', json);
      setFormError(json.error || error || 'Something went wrong, try again');
      onSubmissionError();
    }
  };

  const handleSubmit = async () => {
    setFormError(null);

    const docIds = selectedDocs.map((doc) => doc.id);
    const settings = { user_mode: tool };
    addAdvancedSettingValues(advancedSettingsValues, settings);

    if (!selectedFoundationDocument.length) {
      setFormError('Please select a foundation document');
      return;
    }

    const forceResultId = overrideResultId || selectedOverrideResultId;
    const enteredResultId =
      forceResultId !== null && !['Please select...', 'Enter Manually', ''].includes(forceResultId);
    if (enteredResultId) {
      onSetResult(null);
      await sleep(600);
      onSetResult({ id: forceResultId, question: '', agentName: '' });
      return;
    }

    onSetResult(null);

    if (!agentName) {
      setFormError('Please select an agent');
      return;
    }

    setSubmissionState(submissionStates.generatingResults);

    const result = await submitQueriesStream({
      questions: question,
      agentName,
      docIds,
      settings,
    });
    if (result.status === 200) {
      onSetResult({ id: result.runId, question, agentName });
      addToResultIdStorage(result.runId);
    } else {
      setFormError('Something went wrong, try again.');
      return;
    }
  };

  const onClear = () => {
    setSubmissionState(submissionStates.idle);
    setConstructedXLSFile(null);
    setSelectedDocs([]);
    setSelectedFoundationDocument([]);
    setAgentName('');
    setQuestion('');
    setOverrideResultId('');
    setSelectedOverrideResultId('');
    setShowOverrideResultIdInput(false);
    setShowManualOverrideResultIdInput(false);
    setFormError(null);
    setPageError(null);
    setStreamStatus('pending');
    setResultId(null);
    setResultMetadata(null);
    setOverrideResultIdOptions(defaultResultIdOptions);
  };

  const handleGoBack = () => {
    if (submissionState === submissionStates.finishedXLSFile) {
      setSubmissionState(submissionStates.idle);
      setConstructedXLSFile(null);
      return;
    }

    navigate(ROUTES.home);
  };

  const onSetResult = (resultMetadata) => {
    if (resultMetadata) {
      setResultId(resultMetadata.id);
      setResultMetadata(resultMetadata);
    } else {
      setResultId(null);
      setResultMetadata(null);
    }
  };

  const onDismiss = () => setIsSelectingDocs(false);

  const onSelectedDocsChange = (t) => setSelectedDocs(t);

  const onQuestionChange = (e) => {
    const {
      target: { value },
    } = e;
    setQuestion(value);
  };

  const onSelectDocumentsClick = () => setIsSelectingDocs(true);

  const onAgentChange = (v) => {
    setAgentName(v);
  };

  const onResultComplete = () => {
    setSubmissionState(submissionStates.finishedResult);
  };

  const onSubmissionError = () => {
    setSubmissionState(submissionStates.error);
  };

  const handleStreamStatusChange = (status) => {
    setStreamStatus(status);
    if (status === 'error') {
      onSubmissionError();
    }
  };
  const handleShowOverrideResultIdInputClick = () => {
    setShowOverrideResultIdInput(true);
    const storageResultIds = getResultIdStorage();
    setOverrideResultIdOptions([...defaultResultIdOptions, ...storageResultIds]);
  };

  const handleRevertOverrideResultIdClick = () => {
    setShowOverrideResultIdInput(false);
    setOverrideResultIdOptions(defaultResultIdOptions);
    setSelectedOverrideResultId('');
    setOverrideResultId('');
  };

  React.useEffect(() => {
    if (resultId && submissionState === submissionStates.generatingResults) {
      const id = 'question-answer';
      const yOffset = -10;
      const element = document.getElementById(id);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 600);
      }
    }
  }, [resultId, submissionState]);

  if (pageError) {
    return (
      <PageWrapper>
        <Header />
        <Body withFooter>
          <Page width="1200px">
            <Container top={24} bottom={24}>
              <BackArrowButton onClick={handleGoBack} />
              <Container top={48} bottom={24}>
                <Body5>Something went wrong, refresh the page and try again.</Body5>
              </Container>
            </Container>
          </Page>
        </Body>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width="1200px">
          <Container top={24} bottom={24}>
            <BackArrowButton onClick={handleGoBack} />
            <AdvancedSettingsButton tool={tool} />
          </Container>

          <Container bottom={32}>
            <H5>Spreadsheet from Agent Script</H5>
            {isSelectingDocs ? (
              <DocumentSelectionModalControlled
                documentSelectionModes={docSelectModes}
                onDismiss={onDismiss}
                selectedDocs={selectedDocs}
                onSelectedDocsChange={onSelectedDocsChange}
                tool={tool}
              />
            ) : null}
            {isSelectingFoundationDocument ? (
              <DocumentSelectionModalControlled
                documentSelectionModes={foundationDocSelectModes}
                defaultActiveMode={foundationDocSelectModes[0]}
                onDismiss={() => setIsSelectingFoundationDocument(false)}
                selectedDocs={selectedFoundationDocument}
                onSelectedDocsChange={setSelectedFoundationDocument}
                tool={tool}
                singleDocMode
              />
            ) : null}
            {submissionState === submissionStates.idle ? (
              <FormWrapper>
                <Container>
                  {showOverrideResultIdInput ? (
                    <Container style={{ width: '420px' }}>
                      <Container row verticallyCenter spaceBetween bottom={16}>
                        <Container>
                          <Body5>Override Result ID</Body5>
                        </Container>
                        <Container>
                          <TransparentFlatButton onClick={handleRevertOverrideResultIdClick}>
                            Revert
                          </TransparentFlatButton>
                        </Container>
                      </Container>
                      <Container>
                        <select value={selectedOverrideResultId} onChange={handleSelectedOverrideResultIdChange}>
                          {overrideResultIdOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </Container>
                      {showManualOverrideResultIdInput ? (
                        <Container top={16}>
                          <OverrideResultIdInput
                            type="text"
                            name="override-result-id"
                            value={overrideResultId}
                            placeholder="Please enter the result ID"
                            onChange={handleOverrideResultIdChange}
                          />
                        </Container>
                      ) : null}
                    </Container>
                  ) : (
                    <div>
                      <Container top={8}>
                        <Label>
                          <Body5>Agent</Body5>
                        </Label>
                        <AgentSelectWrapper>
                          <AgentSelect onChange={onAgentChange} value={agentName} />
                        </AgentSelectWrapper>
                      </Container>
                      <Container row verticallyCenter spaceBetween top={24}>
                        <Label>
                          <Body5>Instructions</Body5>
                        </Label>
                        <Container top={16}>
                          <PreviouslyGeneratedInstructionsIdButton>
                            <TransparentFlatButton onClick={handleShowOverrideResultIdInputClick}>
                              Use Previously Generated Instructions ID
                            </TransparentFlatButton>
                          </PreviouslyGeneratedInstructionsIdButton>
                        </Container>
                      </Container>
                      <Container top={8}>
                        <textarea name={'question'} onChange={onQuestionChange} value={question} />
                      </Container>
                      <Container top={16}>
                        <SourcesHeading />
                        <SelectedDocuments
                          onSelectDocumentsClick={onSelectDocumentsClick}
                          selectedDocs={selectedDocs}
                          onSelectedDocsChange={onSelectedDocsChange}
                          showAddAllDocumentsAction={false}
                        />
                      </Container>
                    </div>
                  )}
                </Container>

                <Container top={32}>
                  <FoundationDocHeading />

                  <Container top={0}>
                    {selectedFoundationDocument.length > 0 && (
                      <Container top={8}>
                        <SelectedDocument
                          doc={selectedFoundationDocument[0]}
                          handleRemove={handleRemoveFoundationDocument}
                        />
                      </Container>
                    )}
                    <Container top={16}>
                      {selectedFoundationDocument.length > 0 ? (
                        <SkeletonButton onClick={() => setIsSelectingFoundationDocument(true)}>
                          Replace Foundation Document
                        </SkeletonButton>
                      ) : (
                        <FlatButton onClick={() => setIsSelectingFoundationDocument(true)}>
                          Select Foundation Document
                        </FlatButton>
                      )}
                    </Container>
                  </Container>
                </Container>
                <Container top={48}>
                  <Container centerAll>
                    <FormState error={formError} />
                  </Container>
                  <SubmitButtonWrapper>
                    <FlatButton onClick={handleSubmit} fullWidth>
                      Submit
                    </FlatButton>
                  </SubmitButtonWrapper>
                  <Container top={8} centerAll>
                    <TransparentFlatButton onClick={onClear} fullWidth>
                      Reset
                    </TransparentFlatButton>
                  </Container>
                </Container>
              </FormWrapper>
            ) : null}
            {submissionState === submissionStates.generatingResults ? (
              <Container top={8}>
                <Working message="Generating Results..." />
              </Container>
            ) : null}
            {submissionState === submissionStates.generatingXLSFile ? (
              <Container top={8}>
                <Working message="Generating File..." />
              </Container>
            ) : null}
            {submissionState === submissionStates.finishedXLSFile ? (
              <Container top={8}>
                <Body5>Results generated successfully, click the button below to download the XLS file.</Body5>
                <Container top={16}>
                  <FlatButton onClick={() => onDownloadXLSFile(constructedXLSFile)}>Download XLS File</FlatButton>
                </Container>
              </Container>
            ) : null}
          </Container>
        </Page>
        {resultId && (
          <Page width={'1200px'}>
            <div id={'question-answer'}></div>
            {resultMetadata?.question ? (
              <Container top={16}>
                <Body5>Instructions: {resultMetadata?.question}</Body5>
              </Container>
            ) : null}
            <Container top={16}>
              <AgentScriptResult
                resultId={resultId}
                onResultComplete={onResultComplete}
                onSetStreamStatus={handleStreamStatusChange}
              />
            </Container>
          </Page>
        )}
      </Body>
    </PageWrapper>
  );
}

export default AgentSpreadsheet;
